import { css } from "@linaria/core";

import type { ScalarToken } from "./token.scalar";
import { makeScalarToken } from "./token.scalar";
import type { OverridenTokenCSSProperties } from "./util.make-generic-token";
import { makeGenericToken } from "./util.make-generic-token";

type TransitionTokenPropertyInput = {
  easing: string;

  /**
   * if given, the duration of this specific property will be scaled to the
   * given value
   */
  relativeDuration?: number;

  /**
   * if given, the transition of this specific property will be deferred with
   * this delay relatively to the total transition duration
   */
  relativeDelay?: number;

  /**
   * if provided, then the property transition's duration and delay will be
   * extended by the given factor relatively to the total transition duration
   *
   * @example tests
   * ```ts
   * const reducedTransformTransition = makeTransitionToken("--transform", {
   *   defaultDurationMs: 500,
   *   properties: {
   *     transform: {
   *       easing: easings.inOutCubic,
   *       reducedMotion: {
   *         addedRelativeDuration: 1,
   *         addedRelativeDelay: -1
   *       }
   *     }
   *   }
   * });
   * ```
   *
   * you can play around with these values to accomodate for users who prefer
   * reduced motion, e.g.
   *
   * - by extending the transition duration so that the user only sees the
   *   first part of a slide-out transition (assuming that React is correctly
   *   configured to end the transition before the browser reaches the target)
   *
   * - by extending transition duration and adding equivalent negative delay,
   *   so that the user only sees the last part of a slide-in transition
   */
  reducedMotion?: {
    addedRelativeDuration: number;
    addedRelativeDelay: number;
  };
};

export type TransitionTokenInput<TProps extends string> = {
  defaultDurationMs: number;
  properties: Record<TProps, TransitionTokenPropertyInput>;
};

export type TransitionToken<TProps extends string> = {
  <TExtraProps extends string>(
    options?: {
      durationMs?: number;
      overrides?: Partial<
        Record<
          TProps | TExtraProps,
          (duration: string, easing: string, delay: string) => string
        >
      >;
    } & Partial<Record<TProps, TExtraProps[] | false>>,
  ): string;

  __override: (
    input: Partial<
      Record<
        TProps,
        Partial<{
          easing: ScalarToken | string;
          relativeDuration: ScalarToken | number;
          relativeDelay: ScalarToken | number;
          reducedMotion: Partial<{
            addedRelativeDuration: ScalarToken | number;
            addedRelativeDelay: ScalarToken | number;
          }>;
        }>
      >
    >,
  ) => OverridenTokenCSSProperties;

  __extend(propName: `--${string}`): TransitionToken<TProps>;

  defaultDurationMs: number;
  properties: Record<
    TProps,
    {
      easing: ScalarToken;
      relativeDuration: ScalarToken;
      relativeDelay: ScalarToken;
      reducedMotion: {
        addedRelativeDuration: ScalarToken;
        addedRelativeDelay: ScalarToken;
      };
    }
  >;
};

export function makeTransitionToken<TProps extends string>(
  propName: `--${string}`,
  input: TransitionTokenInput<TProps> | TransitionToken<TProps>,
): TransitionToken<TProps> {
  const propertyKeys = Object.keys(input.properties) as TProps[];
  const tokens = Object.fromEntries(
    propertyKeys.map(
      (key) =>
        [
          key,
          {
            easing: makeScalarToken(
              `${propName}--${key}--easing`,
              input.properties[key].easing,
            ),
            relativeDuration: makeScalarToken(
              `${propName}--${key}--duration`,
              input.properties[key].relativeDuration ?? 1,
            ),
            relativeDelay: makeScalarToken(
              `${propName}--${key}--delay`,
              input.properties[key].relativeDelay ?? 0,
            ),
            reducedMotion: {
              addedRelativeDuration: makeScalarToken(
                `${propName}--${key}--reduce-motion--duration`,
                input.properties[key].reducedMotion?.addedRelativeDuration ?? 0,
              ),
              addedRelativeDelay: makeScalarToken(
                `${propName}--${key}--reduce-motion--delay`,
                input.properties[key].reducedMotion?.addedRelativeDelay ?? 0,
              ),
            },
          },
        ] as const,
    ),
  ) as Record<
    TProps,
    {
      relativeDuration: ScalarToken;
      relativeDelay: ScalarToken;
      easing: ScalarToken;
      reducedMotion: {
        addedRelativeDuration: ScalarToken;
        addedRelativeDelay: ScalarToken;
      };
    }
  >;

  return makeGenericToken(
    function format(
      options?: {
        durationMs?: number;
        overrides?: Partial<
          Record<
            string,
            (duration: string, easing: string, delay: string) => string
          >
        >;
      } & Partial<Record<TProps, string[] | false>>,
    ) {
      const durationMs = `${options?.durationMs ?? input.defaultDurationMs}ms`;

      return propertyKeys
        .flatMap((key) => {
          const cssProps = options?.[key] ?? [key];

          // if this property was explicitly disabled, then skip it entirely
          // from the output
          if (cssProps === false) {
            return [];
          }

          const propTokens = tokens[key];

          // ... add every requested property to the output
          return cssProps.map((cssProp) => {
            const duration = `calc(${durationMs} * (${propTokens.relativeDuration()} + ${propTokens.reducedMotion.addedRelativeDuration()} * var(--reduce-motion, 0)))`;
            const delay = `calc(${durationMs} * (${propTokens.relativeDelay()} + ${propTokens.reducedMotion.addedRelativeDelay()} * var(--reduce-motion, 0)))`;
            const easing = propTokens.easing();

            const transitionConfig =
              options?.overrides?.[cssProp]?.(duration, easing, delay) ??
              `${duration} ${easing} ${delay}`;

            return `${cssProp} ${transitionConfig}`;
          });
        })
        .join(",");
    },

    function override(
      nextValue:
        | Partial<
            Record<
              TProps,
              Partial<{
                easing: ScalarToken | string;
                relativeDuration: ScalarToken | number;
                relativeDelay: ScalarToken | number;
                reducedMotion: Partial<{
                  addedRelativeDuration: ScalarToken | number;
                  addedRelativeDelay: ScalarToken | number;
                }>;
              }>
            >
          >
        | undefined,
    ) {
      if (nextValue === undefined) {
        return {};
      }

      // if given a completely new input, then override the created tokens
      // with the newly given inputs
      return propertyKeys.reduce((acc, key) => {
        const nextProp = nextValue[key];

        if (nextProp === undefined) {
          return acc;
        }

        return {
          ...acc,
          ...tokens[key].easing.__override(nextProp.easing),
          ...tokens[key].relativeDuration.__override(nextProp.relativeDuration),
          ...tokens[key].relativeDelay.__override(nextProp.relativeDelay),
          ...tokens[key].reducedMotion.addedRelativeDuration.__override(
            nextProp.reducedMotion?.addedRelativeDuration,
          ),
          ...tokens[key].reducedMotion.addedRelativeDelay.__override(
            nextProp.reducedMotion?.addedRelativeDelay,
          ),
        };
      }, {});
    },

    function copy(propName, token) {
      return makeTransitionToken(propName, token);
    },

    {
      defaultDurationMs: input.defaultDurationMs,
      properties: tokens,
    },
  );
}

// auto-configure reduce-motion by applying a --reduce-motion variable we can
// use to auto-apply the reduction only when user explicitly requests it
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global() {
    :root {
      @media (prefers-reduced-motion: reduce) {
        --reduce-motion: 1;
      }
    }
  }
`;
